import { Save, Layout, Eye } from "lucide-react";

function UiField({ label, value, onChange, multiline = false, preview, rows }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">{label}</span>
        {preview && <span className="text-[9px] text-fendi/50 italic truncate ml-2 line-clamp-1">{preview(value)}</span>}
      </div>
      <Tag className="w-full text-xs px-3 py-2 border border-fendi/20 rounded-md bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" rows={rows || (multiline ? 6 : undefined)} value={value || ""} onChange={e => onChange(e.target.value)} />
    </label>
  );
}

function UiSection({ title, icon: Icon, description, children, onSave, saveLabel, saving, readOnly }) {
  return (
    <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0">
            {Icon && <Icon size={16} />}
          </div>
          <div>
            <h3 className="text-sm font-medium text-softblack">{title}</h3>
            {description && <p className="text-[10px] text-warmgray mt-0.5">{description}</p>}
          </div>
        </div>
        {!readOnly && (
          <button onClick={onSave} disabled={saving} className="btn-primary !py-2 !px-4 !text-[10px] shrink-0">
            <Save size={12} className="mr-1.5" /> {saving ? "A guardar..." : saveLabel || "Guardar"}
          </button>
        )}
      </div>
      <fieldset disabled={readOnly}>
        <div className="p-6 space-y-5">{children}</div>
      </fieldset>
    </div>
  );
}

export default function AdminInterfaceEditor({ readOnly = false, uiStrings, setUiStrings, saving, handleSaveUiStrings }) {
  const set = (context, key, value) => setUiStrings({ ...uiStrings, [context]: { ...uiStrings[context], [key]: value } });

  return (
    <div data-id="admin-interface" className="animate-in fade-in slide-in-from-bottom-2 duration-500 space-y-6">
      <div className="flex items-center gap-3 px-1">
        <Eye size={16} className="text-champagne" />
        <p className="text-xs text-warmgray">Textos do rodapé do site.</p>
      </div>

      <UiSection title="Textos do Rodapé" icon={Layout} description="Links, títulos e textos no footer"
        onSave={() => handleSaveUiStrings("footer")} saveLabel="Guardar Rodapé" saving={saving} readOnly={readOnly}>
        <div className="grid md:grid-cols-3 gap-x-8 gap-y-5">
          <UiField label="Título: Navegação" value={uiStrings?.footer?.nav_heading} onChange={v => set("footer", "nav_heading", v)}
            preview={v => v || "ex: Navegação"} />
          <UiField label="Título: Contacto" value={uiStrings?.footer?.contact_heading} onChange={v => set("footer", "contact_heading", v)}
            preview={v => v || "ex: Contacto"} />
          <UiField label="Link: Área restrita" value={uiStrings?.footer?.admin_link} onChange={v => set("footer", "admin_link", v)}
            preview={v => v || "ex: Área restrita"} />
        </div>
      </UiSection>

      <UiSection title="Conteúdo do Rodapé" icon={Eye} description="Suporta HTML: &lt;strong&gt;, &lt;b&gt;, &lt;em&gt;, &lt;br&gt;"
        onSave={() => handleSaveUiStrings("general")} saveLabel="Guardar Geral" saving={saving} readOnly={readOnly}>
        <div className="space-y-5">
          <UiField label="Logo (Texto)" value={uiStrings?.general?.logo} onChange={v => set("general", "logo", v)}
            preview={v => v || "ex: Hub"} />
          <UiField label="Slogan / Subtítulo" value={uiStrings?.general?.subtitle} onChange={v => set("general", "subtitle", v)}
            preview={v => v || "ex: Consultoria · Património · Design"} />
          <UiField label="Localizações" value={uiStrings?.general?.locations} onChange={v => set("general", "locations", v)}
            preview={v => v || "ex: Lisboa, Porto"} />
          <UiField label="Descrição Curta (Rodapé)" value={uiStrings?.general?.footer_description} onChange={v => set("general", "footer_description", v)}
            preview={v => v ? v.substring(0, 60) + "..." : "ex: Hub de <strong>excelência</strong>"} multiline />
          <UiField label="Texto de Copyright (Rodapé)" value={uiStrings?.general?.copyright} onChange={v => set("general", "copyright", v)}
            preview={v => v ? v.substring(0, 60) + "..." : "ex: Hub <strong>Profissional</strong>"} multiline />
        </div>
      </UiSection>
    </div>
  );
}
